import _ from 'lodash';
import { DateTime } from 'luxon';

import type {
  Address,
  CounselorMeritBadge,
  GetCompletePersonProfileRes,
  MBCRecord,
  PostSearchMBCRequest,
} from '@appTypes/esb';
import type { PaginationRequest } from '@appTypes/esb/advancements/v2/meritBadges/counselors/search';
import type { SearchFormState } from '@modules/meritBadgeCounselors/types';
import { getPreferredName } from '@shared/utils/preferredName';
import type { NormalizedOrgProfile } from '@unit/types';

import {
  ALL_MERIT_BADGES,
  ANY_COUNCIL_VALUE,
  AvailabilityFilterOptions,
  BOTH_ALL_NO_MERIT_BADGES,
  MBCSearchTableFields,
  NON_US_VALUE,
  NO_MERIT_BADGES,
  proximityFilterId,
} from '../constants';

export type SelectOption = { label: string; value: string };

interface CouncilDistrict {
  organizationFullName: string;
  organizationGuid: string;
}

export const getOrgOptions = (
  unitInfo: NormalizedOrgProfile | undefined,
  districts: CouncilDistrict[],
  flags: {
    hasCouncilLevelPosition: boolean;
    hasDistrictLevelPosition: boolean;
    hasUnitLevelPosition: boolean;
    isDistrict: boolean;
    isUnit: boolean;
  },
): SelectOption[] => {
  const {
    hasCouncilLevelPosition,
    hasDistrictLevelPosition,
    hasUnitLevelPosition,
    isDistrict,
    isUnit,
  } = flags;

  if (hasCouncilLevelPosition) {
    const council =
      unitInfo?.councilName && unitInfo?.councilGuid
        ? [{ label: unitInfo.councilName, value: unitInfo.councilGuid }]
        : [];
    const districtOpts = districts.map(d => ({
      label: d.organizationFullName,
      value: d.organizationGuid,
    }));
    return [...council, ...districtOpts];
  }

  if (isDistrict && hasDistrictLevelPosition && unitInfo?.districtName) {
    return [{ label: unitInfo.districtName, value: unitInfo.districtGuid }];
  }

  if (isUnit && hasUnitLevelPosition && unitInfo) {
    return [
      { label: unitInfo.districtName, value: unitInfo.districtGuid },
      { label: unitInfo.councilName, value: unitInfo.councilGuid },
    ];
  }

  return [];
};

export const generateMBCSearchPayload = (
  searchInfo: SearchFormState,
  currentPagination?: PaginationRequest,
): PostSearchMBCRequest => {
  const basePayload: {
    organizationGuid?: string;
    meritBadges?: number[];
    counselorAvailability?: string;
    zipCode?: string;
    milesAway?: number;
    nonUsCounselors?: boolean;

    firstName?: string;
    lastName?: string;
    email?: string;
    memberId?: number;

    pagination?: {
      page?: number;
      perPage?: number;
      sortBy?: string;
      sortOrder?: string;
    };
  } = {};

  if (searchInfo.orgGuidType === NON_US_VALUE) {
    basePayload.nonUsCounselors = true;
  } else if (searchInfo.orgGuidType === ANY_COUNCIL_VALUE) {
    basePayload.nonUsCounselors = false;
  } else if (searchInfo.orgGuidType) {
    basePayload.organizationGuid = searchInfo.orgGuidType;
  }

  if (searchInfo.zipCode && searchInfo.orgGuidType !== NON_US_VALUE) {
    delete basePayload.nonUsCounselors;
    basePayload.zipCode = searchInfo.zipCode;

    if (searchInfo.proximity !== proximityFilterId.ANY)
      basePayload.milesAway = searchInfo.proximity;
  }

  if (
    searchInfo.meritBadges &&
    ![
      ALL_MERIT_BADGES.value,
      NO_MERIT_BADGES.value,
      BOTH_ALL_NO_MERIT_BADGES.value,
    ].includes(searchInfo.meritBadges)
  )
    basePayload.meritBadges = [searchInfo.meritBadges];

  if (searchInfo.firstName && searchInfo.lastName) {
    basePayload.firstName = _.trim(searchInfo.firstName).replace(/\*/g, '%');
    basePayload.lastName = _.trim(searchInfo.lastName).replace(/\*/g, '%');
    if (searchInfo.email) {
      basePayload.email = _.trim(searchInfo.email);
    }
    if (searchInfo.memberId) {
      basePayload.memberId = Number(_.trim(searchInfo.memberId));
    }
  }

  if (searchInfo.availability !== AvailabilityFilterOptions.BOTH)
    basePayload.counselorAvailability = searchInfo.availability;

  if (currentPagination) basePayload.pagination = currentPagination;

  return basePayload as PostSearchMBCRequest;
};

const isActiveMeritBadge = (mb: CounselorMeritBadge): boolean => {
  if (!mb.effectiveDt) return false;
  const start = DateTime.fromISO(mb.effectiveDt);
  const end = mb.expiryDt ? DateTime.fromISO(mb.expiryDt) : null;
  if (!end || !end.isValid) return true;
  const today = DateTime.now();
  return today >= start && today <= end;
};

export const filterMBCSearchResponse = (
  response: MBCRecord[],
  filterType: string | number = BOTH_ALL_NO_MERIT_BADGES.value,
): MBCRecord[] => {
  let filtered = _.chain(response)
    .map(mbc => ({
      ...mbc,
      meritBadges: _.filter(mbc.meritBadges, isActiveMeritBadge),
    }))
    .value();

  if (filterType === ALL_MERIT_BADGES.value) {
    filtered = filtered.filter(mbc => !_.isEmpty(mbc.meritBadges));
  }
  if (filterType === NO_MERIT_BADGES.value) {
    filtered = filtered.filter(mbc => _.isEmpty(mbc.meritBadges));
  }

  // diff input vs output to find which MBCs were excluded + why
  const keptIds = new Set(_.map(filtered, 'memberId'));
  const excluded = _.chain(response)
    .reject(mbc => keptIds.has(mbc.memberId))
    .map(mbc => ({
      ...mbc,
      _excludeReason: _.some(mbc.meritBadges, isActiveMeritBadge)
        ? 'SYT_EXPIRED'
        : 'NO_ACTIVE_MERIT_BADGES',
    }))
    .value();

  const finalData = { excluded, filtered };
  return finalData.filtered;
};

export const searchOnItems = (searchQuery: string, items: MBCRecord[]) => {
  if (searchQuery) {
    return items.filter(item =>
      [item.memberId, item.firstName, item.lastName]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase()
        .includes(searchQuery.toLowerCase()),
    );
  }

  return items;
};

export const getDefaultSearchType = (
  isCouncil: boolean,
  isDistrict: boolean,
  isUnit: boolean,
  hasCouncilLevelPosition: boolean,
  hasDistrictLevelPosition: boolean,
  hasUnitLevelPosition: boolean,
  unitInfo: NormalizedOrgProfile | undefined,
) => {
  if (
    ((isCouncil && hasCouncilLevelPosition) ||
      (isUnit && hasUnitLevelPosition)) &&
    unitInfo?.councilName &&
    unitInfo?.councilGuid
  ) {
    return unitInfo.councilGuid ?? '';
  }

  if (isDistrict && hasDistrictLevelPosition && unitInfo?.districtName) {
    return unitInfo.districtGuid ?? '';
  }

  return '';
};

export const sortDefaultBySearchType = (
  searchInfo: SearchFormState,
  response: MBCRecord[],
) => {
  if (searchInfo.zipCode) {
    return _.sortBy(response, MBCSearchTableFields.DISTANCE);
  }

  return _.sortBy(response, mbc => getPreferredName(mbc));
};

export const getPrimaryAddress = (
  userProfile: GetCompletePersonProfileRes | undefined,
  property: keyof Address,
) => {
  const primaryAddress = userProfile?.addresses?.find(
    (address: Address) => address.isPrimary,
  );

  return primaryAddress?.[property] ?? '';
};

export const numberWithDecimal = (
  numberValue: number | null,
  decimalPlaces: number = 2,
) => {
  if (numberValue) {
    return _.round(numberValue, decimalPlaces).toFixed(decimalPlaces);
  }
  return 0;
};

export const defaultSortBy = (search: SearchFormState) => {
  if (search.zipCode && search.orgGuidType !== NON_US_VALUE) {
    return MBCSearchTableFields.DISTANCE;
  }
  return MBCSearchTableFields.LAST_NAME;
};
